/**
 * Bridge Health Polling Hook
 * Polls GET /api/health/bridge every 60 seconds and determines
 * bridge alive/dead status based on heartbeat timestamp.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHealthApi } from "@/services/api/health-api";

const BRIDGE_POLL_INTERVAL = 60_000; // 1 minute
// Bridge sends a heartbeat every 15s. Allow 25s before marking dead.
const BRIDGE_ALIVE_THRESHOLD = 25_000; // 25 seconds

export type BridgeStatus = "alive" | "dead" | "loading" | "no-bridge";

interface BridgeHealthState {
    status: BridgeStatus;
    lastTimestamp: string | null;
    isRefreshing: boolean;
}

const NO_BRIDGE_STATE: BridgeHealthState = {
    status: "no-bridge",
    lastTimestamp: null,
    isRefreshing: false,
};

export function useBridgeHealth(enabled = true) {
    const [state, setState] = useState<BridgeHealthState>({
        status: "loading",
        lastTimestamp: null,
        isRefreshing: false,
    });

    const healthApi = useHealthApi();
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isPollInProgressRef = useRef(false);
    const pollRef = useRef<(() => Promise<void>) | null>(null);

    const poll = useCallback(async () => {
        if (isPollInProgressRef.current) return;
        isPollInProgressRef.current = true;
        setState((prev) => ({ ...prev, isRefreshing: true }));

        try {
            const response = await healthApi.getBridgeHealth();
            const heartbeatTime = new Date(response.timestamp).getTime();
            const isAlive = Date.now() - heartbeatTime < BRIDGE_ALIVE_THRESHOLD;

            setState({
                status: isAlive ? "alive" : "dead",
                lastTimestamp: response.timestamp,
                isRefreshing: false,
            });
        } catch {
            // 404 = no heartbeat found, any other error = also treat as dead
            setState((prev) => ({
                status: "dead",
                lastTimestamp: prev.lastTimestamp,
                isRefreshing: false,
            }));
        } finally {
            isPollInProgressRef.current = false;
        }
    }, [healthApi]);

    // Keep ref updated so interval always calls latest version
    pollRef.current = poll;

    useEffect(() => {
        if (!enabled) return;

        // Poll immediately on mount
        pollRef.current?.();

        // Start interval
        intervalRef.current = setInterval(() => {
            pollRef.current?.();
        }, BRIDGE_POLL_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled]);

    /** Manual refresh — polls immediately and resets the interval timer (no-op when disabled) */
    const refresh = useCallback(() => {
        if (!enabled) return;
        pollRef.current?.();

        // Reset interval so next auto-poll is a full cycle away
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            pollRef.current?.();
        }, BRIDGE_POLL_INTERVAL);
    }, [enabled]);

    return useMemo(() => (enabled ? { ...state, refresh } : { ...NO_BRIDGE_STATE, refresh }), [enabled, state, refresh]);
}
